'use client'

import { cn } from '@/utilities/ui'
import React, { useEffect, useRef, useState } from 'react'

import type { Props as MediaProps } from '../types'

import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getBunnyGuidForFilename } from '@/components/Video/bunny'
import { BunnyBgVideo } from '@/components/Video/BunnyBgVideo'

export const VideoMedia: React.FC<MediaProps> = (props) => {
  const { onClick, resource, videoClassName } = props

  const videoRef = useRef<HTMLVideoElement>(null)
  // Defer buffering until the video nears the viewport (PageSpeed 2026-08-18:
  // a below-fold 7MB video block downloaded on page load). Identical for the
  // visitor — the loop starts as they reach it.
  const [active, setActive] = useState(false)

  useEffect(() => {
    const { current: video } = videoRef
    if (!video) return
    if (!('IntersectionObserver' in window)) {
      setActive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true)
          io.disconnect()
        }
      },
      { rootMargin: '400px' },
    )
    io.observe(video)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!active || !video) return
    // A <source> appended after mount needs an explicit load() before play()
    video.load()
    video.play().catch(() => {})
  }, [active])

  if (resource && typeof resource === 'object') {
    const { filename } = resource

    // Mirrored to Bunny Stream — serve the adaptive embed instead of the MP4
    const bunnyGuid = getBunnyGuidForFilename(filename)
    if (bunnyGuid) return <BunnyBgVideo guid={bunnyGuid} />

    return (
      <video
        autoPlay
        className={cn(videoClassName)}
        controls={false}
        loop
        muted
        onClick={onClick}
        playsInline
        preload={active ? 'auto' : 'none'}
        ref={videoRef}
      >
        {active && <source src={getMediaUrl(`/media/${filename}`)} />}
      </video>
    )
  }

  return null
}
